import CONFIG, { Config } from './config'
import Matter from 'matter-js'
import debounce from 'lodash.debounce'

const DEFAULT_SETTINGS = {
  height: 600,
  height_mb: 400,
  background_color: 'transparent',
  text_color: 'white'
}

interface IPhysicsBall extends Matter.Body {
  labelElement: HTMLDivElement;
  radius: number;
}

interface SectionSettings {
  height: number;
  height_mb: number;
  background_color: string;
  text_color: string;
  colours?: string;
  letters?: string;
  show_letters?: boolean;
  fill_percentage?: number;
}

export class PhysicsBalls extends HTMLElement {
  private balls: IPhysicsBall[]
  private letterIndex: number
  private readonly config: Config
  private isInViewport: boolean
  private bottomWall: Matter.Body | null
  private container: HTMLDivElement
  private sectionSettings: SectionSettings
  private colourOptions!: string[]
  private letters!: string[]
  private wrappingFooter: HTMLElement
  private runner!: Matter.Runner | null
  private render!: Matter.Render | null
  private engine!: Matter.Engine | null
  private world!: Matter.World | null
  shadowRoot: ShadowRoot

  constructor() {
    super()
    this.shadowRoot = this.attachShadow({ mode: 'open' })
    this.balls = []
    this.letterIndex = 0
    this.config = CONFIG
    this.isInViewport = true
    this.bottomWall = null

    // Create container for Matter.js canvas
    this.container = document.createElement('div')
    this.container.id = 'physics-container'
    this.sectionSettings = this.getSectionSettings()
    this.updateConfig(this.sectionSettings)

    this.wrappingFooter = document.querySelector('footer') || this

    const style = document.createElement('style')
    style.textContent = `
      :host {
          display: block;
          width: 100%;
          height: 100%;
          background-color: ${this.sectionSettings.background_color};
      }

      #physics-container {
          width: 100%;
          height:  100%;
          position: relative;
          overflow: hidden;
      }

      @media (max-width: 768px) {
          :host {
              height: 100%;
          }
          #physics-container {
              height: 100%
          }

      }
      
      .ball-label {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 119%;
          color: ${this.sectionSettings.text_color};
          font-family: Happy Font, sans-serif;
          font-weight: bold;
          pointer-events: none;
          user-select: none;
          will-change: transform;
      }

      .ball-label span {
          display: block;
          line-height: 145%;
          text-align: center;
          width: 100%;
          height: 100%;
      }
    `
    this.shadowRoot.append(style, this.container)

    // Bind methods
    this.handleScroll = this.handleScroll.bind(this)
    this.checkViewportVisibility = this.checkViewportVisibility.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.checkViewportVisibility = this.checkViewportVisibility.bind(this)
    this.handleResize = debounce(this.handleResize.bind(this), 250)
    this.handleDeviceOrientation = this.handleDeviceOrientation.bind(this)
  }

  static get observedAttributes(): string[] {
    return ['settings']
  }

  updateConfig(settings: SectionSettings) {
    this.colourOptions = settings.colours ? JSON.parse(settings.colours) : this.config.colors
    this.letters = settings.letters ? JSON.parse(settings.letters) : this.config.letters
    this.config.fillArea = settings.fill_percentage ? settings.fill_percentage / 10 : this.config.fillArea
  }

  connectedCallback() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.initializePhysics()
            observer.disconnect() // Clean up after initialization
          }
        })
      },
      {
        rootMargin: '100px', // Triggers when element is within 100px of viewport
        threshold: 0
      }
    )

    observer.observe(this)
  }

  disconnectedCallback() {
    if (this.runner) {
      Matter.Runner.stop(this.runner)
    }
    if (this.render) {
      Matter.Render.stop(this.render)
    }
    if (this.handleResize) window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('deviceorientation', this.handleDeviceOrientation)
  }

  private getSectionSettings(): SectionSettings {
    const settings = this.getAttribute('settings')
    // Parse section settings from JSON string
    if (settings) {
      try {
        const parsedSettings = JSON.parse(settings)
        return { ...DEFAULT_SETTINGS, ...parsedSettings }
      } catch (error) {
        console.error('Error parsing section settings:', error)
        return DEFAULT_SETTINGS
      }
    } else {
      return DEFAULT_SETTINGS
    }
  }

  initializePhysics() {
    const { engine, render, world } = this.initializeMatter()
    this.engine = engine
    this.render = render
    this.world = world

    this.createBoundaries()

    // Update loop
    Matter.Events.on(this.engine, 'afterUpdate', () => {
      this.updateLabels()
      this.checkBoundaries()
    })

    // Add collision handling
    Matter.Events.on(this.engine, 'collisionStart', (event) => {
      event.pairs.forEach((pair) => {
        const bodyA = pair.bodyA
        const bodyB = pair.bodyB

        // Only apply rotation to non-static bodies (i.e., the balls)
        if (!bodyA.isStatic) {
          const impulse = pair.collision.normal.x * pair.collision.depth
          const cappedVelocity = Math.min(bodyA.angularVelocity + impulse * 0.005, 0.02)
          Matter.Body.setAngularVelocity(bodyA, cappedVelocity)
        }
        if (!bodyB.isStatic) {
          const impulse = pair.collision.normal.x * pair.collision.depth
          const cappedVelocity = Math.max(bodyB.angularVelocity - impulse * 0.005, -0.02)
          Matter.Body.setAngularVelocity(bodyB, cappedVelocity)
        }
      })
    })

    // Start the engine and renderer
    this.runner = Matter.Runner.create()
    Matter.Runner.run(this.runner, this.engine)
    Matter.Render.run(this.render)
    this.setupMouseInteraction()
    this.setupWindowScroll()
    this.setupDeviceOrientation()
    this.createBalls()
    this.limitMaxSpeed()

    window.addEventListener('resize', debounce(this.handleResize, 250))
  }

  handleResize() {
    if (!this.render) return
    const container = this.container
    const width = container.clientWidth
    const height = container.clientHeight
    // Check if width has changed
    if (width === this.render.options.width) {
      return
    }

    // Update render properties
    this.render.canvas.width = width
    this.render.canvas.height = height
    this.render.options.width = width
    this.render.options.height = height

    // Update render bounds
    Matter.Render.lookAt(this.render, {
      min: { x: 0, y: 0 },
      max: { x: width, y: height }
    })

    // Update walls
    this.updateBoundaries()

    // Reset simulation with new ball count
    this.resetSimulation()
  }

  initializeMatter() {
    const engine = Matter.Engine.create()
    const render = Matter.Render.create({
      element: this.container,
      engine,
      options: {
        width: this.container.clientWidth,
        height: this.container.clientHeight,
        wireframes: false,
        background: 'transparent'
      }
    })

    return { engine, render, world: engine.world }
  }

  calculateBallCount() {
    const dims = this.calculateScaledDimensions()
    // Ensure we have at least enough balls for all letters
    return Math.max(
      this.sectionSettings.show_letters ? this.letters.length : 0,
      Math.min(dims.recommendedCount, this.config.maxBallCount)
    )
  }

  private createBall(x: number, y: number, letter: string, color: string): IPhysicsBall {
    const dims = this.calculateScaledDimensions()
    // Random radius between calculated min and max
    const radius = dims.minRadius

    const ball = Matter.Bodies.circle(x, y, radius, {
      render: {
        fillStyle: color
      },
      restitution: this.config.bounce,
      friction: this.config.friction,
      density: this.config.density,
      frictionAir: this.config.frictionAir,
      torque: 0
    }) as IPhysicsBall

    if (this.sectionSettings.show_letters) {
      // Create label element with responsive font size
      const label = document.createElement('div')
      const span = document.createElement('span')
      label.className = 'ball-label'
      label.style.width = `${radius * 2}px`
      label.style.height = `${radius * 2}px`
      // Scale font size slightly smaller than ball size for better fit
      label.style.fontSize = `${radius * 1.5}px`
      span.textContent = letter
      label.appendChild(span)
      this.container.appendChild(label)

      ball.labelElement = label
    }

    ball.radius = radius
    return ball
  }

  getColourOptions(ballCount: number) {
    let colours: string[] = []
    // Keep adding colors until we have enough
    while (colours.length < ballCount) {
      colours = colours.concat(this.colourOptions)
    }

    // Trim to the exact size needed
    if (colours.length > ballCount) {
      colours = colours.slice(0, ballCount)
    }

    // Randomize the order
    return colours.sort(() => Math.random() - 0.5)
  }

  async createBalls() {
    if (!this.world || !this.render) return
    const { dropDelay, startHeight } = this.config
    const ballCount = this.calculateBallCount()
    const width = this.render.options.width || 500
    const dims = this.calculateScaledDimensions()
    // Create array of colour options with even distribution in random order
    const colourOptions = this.getColourOptions(ballCount)

    for (let i = 0; i < ballCount; i++) {
      // Improved distribution across width
      // Add some randomness to x position while maintaining general spacing
      const baseX = (width * (i % this.letters.length + 0.5)) / this.letters.length
      const jitter = width * 0.05 * (Math.random() - 0.5) // 5% random offset
      const x = Math.max(dims.maxRadius,
        Math.min(width - dims.maxRadius, baseX + jitter))

      const color = colourOptions[i]
      const letter = this.letters[this.letterIndex]

      const ball = this.createBall(x, startHeight, letter, color)
      this.balls.push(ball)
      Matter.World.add(this.world, ball)

      this.letterIndex = (this.letterIndex + 1) % this.letters.length

      await new Promise(resolve => setTimeout(resolve, dropDelay))
    }
  }

  checkViewportVisibility() {
    const rect = this.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const wasInViewport = this.isInViewport

    // Check if element is in viewport
    this.isInViewport = rect.top < windowHeight && rect.bottom > 0

    // Handle simulation pause/resume
    if (this.isInViewport !== wasInViewport) {
      if (this.isInViewport) {
        this.resumeSimulation()
      } else {
        this.pauseSimulation()
      }
    }

    // Update bottom wall if needed
    if (this.isInViewport && this.bottomWall) {
      const viewportBottom = windowHeight
      const elementBottom = rect.bottom
      const newY = Math.min(elementBottom, viewportBottom) - rect.top
      Matter.Body.setPosition(this.bottomWall, {
        x: this.bottomWall.position.x,
        y: newY + 50 // Keep the +50 offset from original code
      })
    }
  }

  handleScroll() {
    this.checkViewportVisibility()
  }

  calculateScaledDimensions() {
    const containerWidth = this.container.clientWidth
    const containerHeight = this.container.clientHeight
    const area = containerWidth * containerHeight

    // Only factor in letter count if config.show_letters is true
    const letterCount = this.sectionSettings.show_letters ? this.letters.length : 0

    // Calculate radius to fit all letters horizontally if needed
    let horizontalRadius
    if (this.sectionSettings.show_letters && letterCount > 0) {
      // We'll leave 10% padding on the sides (thus the 0.9)
      // And account for spacing between balls (thus the 2.5 multiplier for spacing)
      horizontalRadius = (containerWidth * 0.9) / (letterCount * 2.5)
    } else {
      // If not showing letters, base radius on container dimensions
      horizontalRadius = Math.min(containerWidth, containerHeight) * 0.05
    }

    // Scale radius based on screen size
    const screenScale = containerWidth / 1000 // baseline of 1000px
    const scaledRadius = horizontalRadius * screenScale

    // Clamp the radius between reasonable bounds relative to screen size
    const minRadius = Math.max(this.config.minRadius, containerWidth * 0.02)
    const maxRadius = Math.min(
      this.config.maxRadius,
      containerWidth * (this.sectionSettings.show_letters ? Math.max(0.1, letterCount / 20) : 0.1)
    )

    const finalRadius = Math.min(maxRadius, Math.max(minRadius, scaledRadius))

    // Calculate how many balls we need to fill the target area
    // The fill area is relative to the total container area
    const targetArea = area * this.config.fillArea

    // Each ball takes up space that's approximately a square of 2*radius
    // This accounts for spacing between balls more realistically
    const effectiveBallArea = (finalRadius * 2) * (finalRadius * 2)

    // Calculate recommended count with a more conservative approach
    // This will result in fewer balls than the pure mathematical calculation
    const recommendedCount = Math.ceil(targetArea / effectiveBallArea * 0.75)

    return {
      minRadius: finalRadius * 0.8,
      maxRadius: finalRadius * 1.2,
      recommendedCount
    }
  }

  createBoundaries() {
    if (!this.render || !this.world) return

    if (!this.render.options.width || !this.render.options.height) {
      console.error('Missing render dimensions')
      return
    }

    const width = this.render.options.width
    const height = this.render.options.height

    // Create bottom wall with stored reference
    this.bottomWall = Matter.Bodies.rectangle(width / 2, height + 50, width, 100, { isStatic: true })

    const walls = [
      this.bottomWall, // floor
      Matter.Bodies.rectangle(-50, height / 2, 100, height * 4, { isStatic: true }), // left wall
      Matter.Bodies.rectangle(width + 50, height / 2, 100, height * 4, { isStatic: true }), // right wall
      Matter.Bodies.rectangle(width / 2, -100, width, 100, { isStatic: true }) // ceiling
    ]

    Matter.World.add(this.world, walls)

    // Initial viewport check for bottom wall position
    this.checkViewportVisibility()
  }

  updateBoundaries() {
    if (!this.render || !this.world) return
    // Remove old boundaries
    const bodies = Matter.Composite.allBodies(this.world)
    bodies.forEach(body => {
      if (body.isStatic && this.world !== null) {
        Matter.World.remove(this.world, body)
      }
    })

    // Reset bottomWall reference
    this.bottomWall = null

    // Create new boundaries
    this.createBoundaries()
  }

  checkBoundaries() {
    if (!this.render || !this.world) return

    if (!this.render.options.width || !this.render.options.height) {
      console.error('Missing render dimensions')
      return
    }
    const width = this.render.options.width
    const height = this.render.options.height
    const padding = 100 // Extra padding to account for high-speed movements
    const colourOptions = this.getColourOptions(this.balls.length)
    this.balls.forEach((ball, index) => {
      if (ball.position.x < -padding ||
          ball.position.x > width + padding ||
          ball.position.y < -padding ||
          ball.position.y > height + padding) {
        // Remove the old ball
        if (ball.labelElement) {
          ball.labelElement.remove()
        }
        Matter.World.remove(this.world as Matter.World, ball)

        // Create a new ball to replace it
        const color = colourOptions[index]
        const letter = this.letters[this.letterIndex]
        const newBall = this.createBall(width / 2, 0, letter, color) // Drop from top center

        // Update references
        this.balls[index] = newBall
        Matter.World.add(this.world as Matter.World, newBall)

        this.letterIndex = (this.letterIndex + 1) % this.letters.length
      }
    })
  }

  updateLabels() {
    this.balls.forEach(ball => {
      if (ball.labelElement) {
        // Account for container's position, scroll offset, and rotation
        const x = ball.position.x - ball.radius
        const y = ball.position.y - ball.radius
        const angle = ball.angle * (180 / Math.PI) // Convert radians to degrees
        ball.labelElement.style.transform = `translate(${x}px, ${y}px) rotate(${angle}deg)`

        // Set the correct positioning context
        ball.labelElement.style.position = 'absolute'
        ball.labelElement.style.left = '0'
        ball.labelElement.style.top = '0'
      }
    })
  }

  setupWindowScroll() {
    window.addEventListener('scroll', this.handleScroll)
  }

  setupMouseInteraction() {
    if (!this.engine || !this.render || !this.world) return
    const mouse = Matter.Mouse.create(this.wrappingFooter)

    const mouseConstraint = Matter.MouseConstraint.create(this.engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: {
          visible: false
        }
      }
    })

    // @ts-expect-error - missing types
    mouseConstraint.mouse.element.removeEventListener('wheel', mouseConstraint.mouse.mousewheel)
    // @ts-expect-error - missing types
    mouseConstraint.mouse.element.removeEventListener('DOMMouseScroll', mouseConstraint.mouse.mousewheel)
    // @ts-expect-error - missing types
    mouseConstraint.mouse.element.removeEventListener('touchstart', mouseConstraint.mouse.mousedown)
    // @ts-expect-error - missing types
    mouseConstraint.mouse.element.removeEventListener('touchend', mouseConstraint.mouse.mouseup)
    // @ts-expect-error - missing types
    mouseConstraint.mouse.element.removeEventListener('touchmove', mouseConstraint.mouse.mousemove)

    Matter.World.add(this.world, mouseConstraint)
    this.render.mouse = mouse

    // Repel balls from mouse position
    Matter.Events.on(this.engine, 'beforeUpdate', () => {
      const bodies = Matter.Composite.allBodies(this.world as Matter.World)
      bodies.forEach(body => {
        if (!body.isStatic) {
          const distance = Matter.Vector.magnitude(
            Matter.Vector.sub(body.position, mouse.position)
          )

          if (distance < this.config.repelRadius) {
            const force = Matter.Vector.mult(
              Matter.Vector.normalise(
                Matter.Vector.sub(body.position, mouse.position)
              ),
              this.config.repelStrength * (this.config.repelRadius - distance)
            )
            Matter.Body.applyForce(body, body.position, force)
          }
        }
      })
    })
  }

  private handleDeviceOrientation(event: DeviceOrientationEvent): void {
    if (!this.engine) return
    const gravity = this.engine.gravity

    if (event.beta !== null && event.gamma !== null) {
    // Convert angles to radians
      const betaRad = (event.beta) * Math.PI / 180
      const gammaRad = (event.gamma) * Math.PI / 180

      // Calculate gravity components
      // Beta controls y-axis (tilt forward/backward)
      // Gamma controls x-axis (tilt left/right)
      gravity.x = Math.sin(gammaRad) * this.config.gravityScale
      gravity.y = Math.sin(betaRad) * this.config.gravityScale

      // Normalize gravity vector to maintain consistent strength
      const magnitude = Math.sqrt(gravity.x * gravity.x + gravity.y * gravity.y)
      if (magnitude !== 0) {
        gravity.x = (gravity.x / magnitude) * this.config.gravityScale
        gravity.y = (gravity.y / magnitude) * this.config.gravityScale
      }

      // Set a minimum gravity threshold to prevent floating
      const minGravity = 0.001 * this.config.gravityScale
      if (Math.abs(gravity.y) < minGravity) {
        gravity.y = Math.sign(gravity.y) * minGravity
      }
    } else {
    // Default gravity if no orientation data
      gravity.x = 0
      gravity.y = 1 * this.config.gravityScale
    }
  }

  setupDeviceOrientation() {
    if (DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', this.handleDeviceOrientation)
    } else {
      console.warn('Device orientation not supported')
    }
  }

  resetSimulation() {
    if (!this.world) return
    // Clear all non-static bodies from the world
    const bodies = Matter.Composite.allBodies(this.world)
    bodies.forEach(body => {
      if (!body.isStatic) {
        Matter.Composite.remove(this.world as Matter.World, body)
      }
    })

    // Remove all existing labels
    this.balls.forEach(ball => {
      if (ball.labelElement) {
        ball.labelElement.remove()
      }
    })

    // Clear the balls array and reset letter index
    this.balls = []
    this.letterIndex = 0

    // Create new balls with updated config
    this.createBalls()
  }

  limitMaxSpeed() {
    const maxSpeed = this.config.maxVelocity
    Matter.Events.on(this.engine, 'afterUpdate', () => {
      this.balls.forEach((ball) => {
        Matter.Body.setVelocity(ball, {
          x: Math.min(maxSpeed, Math.max(-maxSpeed, ball.velocity.x)),
          y: Math.min(maxSpeed, Math.max(-maxSpeed, ball.velocity.y))
        })
      })
    })
  }

  pauseSimulation() {
    if (this.runner) {
      Matter.Runner.stop(this.runner)
    }
    if (this.render) {
      Matter.Render.stop(this.render)
    }
  }

  resumeSimulation() {
    if (!this.engine) return

    if (this.runner) {
      Matter.Runner.run(this.runner, this.engine)
    }
    if (this.render) {
      Matter.Render.run(this.render)
    }
  }
}
